<template>
  <div class="security-alerts-page">
    <div class="page-header">
      <h1><i class="fas fa-shield-alt"></i> Centre de Sécurité</h1>
      <div class="header-actions">
        <button class="btn-refresh" @click="loadAlerts">
          <i class="fas fa-sync-alt"></i> Actualiser
        </button>
        <div class="search-container">
          <input
              type="text"
              v-model="searchQuery"
              placeholder="Rechercher..."
              @input="filterAlerts"
          />
          <i class="fas fa-search"></i>
        </div>
      </div>
    </div>

    <div class="alert-stats">
      <div class="stat-card" :class="{ 'has-alerts': criticalAlerts > 0 }">
        <div class="stat-icon"><i class="fas fa-exclamation-triangle"></i></div>
        <div class="stat-details">
          <h3>{{ criticalAlerts }}</h3>
          <p>Alertes Critiques</p>
        </div>
      </div>
      <div class="stat-card" :class="{ 'has-alerts': warningAlerts > 0 }">
        <div class="stat-icon"><i class="fas fa-exclamation-circle"></i></div>
        <div class="stat-details">
          <h3>{{ warningAlerts }}</h3>
          <p>Avertissements</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><i class="fas fa-check-circle"></i></div>
        <div class="stat-details">
          <h3>{{ resolvedAlerts }}</h3>
          <p>Alertes Résolues</p>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon"><i class="fas fa-calendar-check"></i></div>
        <div class="stat-details">
          <h3>{{ lastCheck }}</h3>
          <p>Dernière Vérification</p>
        </div>
      </div>
    </div>

    <div class="alerts-filter">
      <div class="filter-group">
        <label>Type d'alerte:</label>
        <select v-model="filterType" @change="filterAlerts">
          <option value="all">Toutes</option>
          <option value="DUPLICATE_CARD_ID">Carte en double</option>
          <option value="DUPLICATE_FINGERPRINT">Empreinte en double</option>
          <option value="AUTHENTICATION_FAILURE">Échec d'authentification</option>
          <option value="ABNORMAL_ACCESS">Accès anormal</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Période:</label>
        <select v-model="filterPeriod" @change="filterAlerts">
          <option value="all">Toute période</option>
          <option value="today">Aujourd'hui</option>
          <option value="week">Cette semaine</option>
          <option value="month">Ce mois</option>
        </select>
      </div>

      <div class="filter-group">
        <label>Statut:</label>
        <select v-model="filterStatus" @change="filterAlerts">
          <option value="all">Tous</option>
          <option value="NEW">Nouveaux</option>
          <option value="IN_PROGRESS">En cours</option>
          <option value="RESOLVED">Résolus</option>
        </select>
      </div>
    </div>

    <div class="alerts-container">
      <div v-if="loading" class="loading-overlay">
        <div class="spinner"></div>
        <p>Chargement des alertes...</p>
      </div>

      <div v-if="!loading && filteredAlerts.length === 0" class="no-alerts">
        <i class="fas fa-check-circle"></i>
        <h3>Aucune alerte</h3>
        <p>Tout est en ordre ! Aucune alerte ne correspond à vos critères.</p>
      </div>

      <transition-group name="alert-list" tag="div" class="alerts-list">
        <div
            v-for="alert in filteredAlerts"
            :key="alert.id"
            class="alert-card"
            :class="{
            'critical': alert.severity === 'CRITICAL',
            'warning': alert.severity === 'WARNING',
            'resolved': alert.status === 'RESOLVED'
          }"
        >
          <div class="alert-header">
            <div class="alert-icon">
              <i class="fas" :class="getAlertIcon(alert.eventType)"></i>
            </div>
            <div class="alert-type">{{ getAlertTypeLabel(alert.eventType) }}</div>
            <div class="alert-severity" :class="alert.severity.toLowerCase()">
              {{ alert.severity }}
            </div>
            <div class="alert-actions">
              <button class="btn-details" @click="showAlertDetails(alert)">
                <i class="fas fa-eye"></i>
              </button>
              <button v-if="alert.status !== 'RESOLVED'" class="btn-resolve" @click="resolveAlert(alert)">
                <i class="fas fa-check"></i>
              </button>
            </div>
          </div>

          <div class="alert-content">
            <div class="alert-info">
              <div class="alert-title">{{ getAlertTitle(alert) }}</div>
              <div class="alert-date">{{ formatDate(alert.eventDate) }}</div>
            </div>
            <div class="alert-description">
              {{ getAlertDescription(alert) }}
            </div>
            <div class="alert-details" v-if="alert.showDetails">
              <div class="detail-row">
                <span class="detail-label">ID d'incident:</span>
                <span class="detail-value">{{ alert.id }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Identifiant:</span>
                <span class="detail-value">{{ alert.identifier }}</span>
              </div>
              <div class="detail-row" v-if="alert.tenantsInvolved">
                <span class="detail-label">Tenants impliqués:</span>
                <span class="detail-value">{{ alert.tenantsInvolved }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Adresse IP:</span>
                <span class="detail-value">{{ alert.ipAddress }}</span>
              </div>
              <div class="detail-row" v-if="alert.additionalInfo">
                <span class="detail-label">Informations supplémentaires:</span>
                <span class="detail-value">{{ alert.additionalInfo }}</span>
              </div>
            </div>
          </div>
        </div>
      </transition-group>
    </div>

    <!-- Modal de détails d'alerte -->
    <div class="modal" v-if="selectedAlert">
      <div class="modal-content">
        <div class="modal-header">
          <h2>Détails de l'alerte</h2>
          <button class="close-btn" @click="selectedAlert = null">&times;</button>
        </div>
        <div class="modal-body">
          <div class="detail-section">
            <h3>Informations générales</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <label>Type d'alerte:</label>
                <div>{{ getAlertTypeLabel(selectedAlert.eventType) }}</div>
              </div>
              <div class="detail-item">
                <label>Sévérité:</label>
                <div class="severity-badge" :class="selectedAlert.severity.toLowerCase()">
                  {{ selectedAlert.severity }}
                </div>
              </div>
              <div class="detail-item">
                <label>Statut:</label>
                <div class="status-badge" :class="selectedAlert.status.toLowerCase()">
                  {{ getStatusLabel(selectedAlert.status) }}
                </div>
              </div>
              <div class="detail-item">
                <label>Date:</label>
                <div>{{ formatDate(selectedAlert.eventDate, true) }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section">
            <h3>Identifiants concernés</h3>
            <div class="detail-grid">
              <div class="detail-item">
                <label>Identifiant:</label>
                <div>{{ selectedAlert.identifier }}</div>
              </div>
              <div class="detail-item">
                <label>Adresse IP:</label>
                <div>{{ selectedAlert.ipAddress }}</div>
              </div>
            </div>
          </div>

          <div class="detail-section" v-if="selectedAlert.tenantsInvolved">
            <h3>Tenants impliqués</h3>
            <div class="tenants-list">
              <div class="tenant-item" v-for="(tenant, index) in selectedAlert.tenantsInvolved.split(',')" :key="index">
                {{ tenant.trim() }}
              </div>
            </div>
          </div>

          <div class="detail-section" v-if="selectedAlert.additionalInfo">
            <h3>Informations supplémentaires</h3>
            <div class="additional-info">
              {{ selectedAlert.additionalInfo }}
            </div>
          </div>

          <div class="action-buttons">
            <button
                v-if="selectedAlert.status !== 'RESOLVED'"
                class="btn btn-resolve"
                @click="resolveAlertAndCloseModal(selectedAlert)"
            >
              <i class="fas fa-check"></i> Marquer comme résolu
            </button>
            <button class="btn btn-investigate">
              <i class="fas fa-search"></i> Investiguer
            </button>
            <button class="btn btn-report">
              <i class="fas fa-file-alt"></i> Générer un rapport
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "../../config/api";
import moment from "moment/moment";

export default {
  name: 'SecurityAlertsPage',
  data() {
    return {
      alerts: [],
      filteredAlerts: [],
      loading: true,
      searchQuery: '',
      filterType: 'all',
      filterPeriod: 'all',
      filterStatus: 'all',
      selectedAlert: null,
      lastCheck: this.formatTime(new Date())
    };
  },
  computed: {
    criticalAlerts() {
      return this.alerts.filter(alert =>
          alert.severity === 'CRITICAL' && alert.status !== 'RESOLVED').length;
    },
    warningAlerts() {
      return this.alerts.filter(alert =>
          alert.severity === 'WARNING' && alert.status !== 'RESOLVED').length;
    },
    resolvedAlerts() {
      return this.alerts.filter(alert => alert.status === 'RESOLVED').length;
    }
  },
  mounted() {
    this.loadAlerts();
    // Rafraîchir automatiquement toutes les 5 minutes
    setInterval(this.loadAlerts, 5 * 60 * 1000);
  },
  methods: {
    async loadAlerts() {
      this.loading = true;
      try {
        // Appel API vers votre backend
        const response = await apiClient.get(
            `${this.$store.getters.user.baseUrl}/security/events`
        );

        this.alerts = response.data;
        this.filterAlerts();
        this.lastCheck = this.formatTime(new Date());
      } catch (error) {
        console.error('Erreur lors du chargement des alertes:', error);
        // Ajoutez ici la gestion d'erreur, comme l'affichage d'une notification
      } finally {
        this.loading = false;
      }
    },
    filterAlerts() {
      let filtered = [...this.alerts];

      // Filtrer par recherche
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(alert =>
            alert.identifier.toLowerCase().includes(query) ||
            alert.ipAddress.toLowerCase().includes(query) ||
            (alert.tenantsInvolved && alert.tenantsInvolved.toLowerCase().includes(query))
        );
      }

      // Filtrer par type
      if (this.filterType !== 'all') {
        filtered = filtered.filter(alert => alert.eventType === this.filterType);
      }

      // Filtrer par période
      if (this.filterPeriod !== 'all') {
        const now = new Date();
        let compareDate;

        if (this.filterPeriod === 'today') {
          compareDate = new Date(now.setHours(0, 0, 0, 0));
        } else if (this.filterPeriod === 'week') {
          compareDate = new Date(now.setDate(now.getDate() - now.getDay()));
        } else if (this.filterPeriod === 'month') {
          compareDate = new Date(now.setDate(1));
        }

        filtered = filtered.filter(alert => new Date(alert.eventDate) >= compareDate);
      }

      // Filtrer par statut
      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(alert => alert.status === this.filterStatus);
      }

      // Trier par date, les plus récents en premier
      filtered.sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));

      this.filteredAlerts = filtered;
    },
    showAlertDetails(alert) {
      this.selectedAlert = {...alert};
    },
    async resolveAlert(alert) {
      try {
        await apiClient.get(
            `${this.$store.getters.user.baseUrl}/security/events/${alert.id}/resolve`
        );

        // Mettre à jour l'alerte localement
        const index = this.alerts.findIndex(a => a.id === alert.id);
        if (index !== -1) {
          this.alerts[index].status = 'RESOLVED';
          this.filterAlerts();
        }
      } catch (error) {
        console.error('Erreur lors de la résolution de l\'alerte:', error);
      }
    },
    resolveAlertAndCloseModal(alert) {
      this.resolveAlert(alert);
      this.selectedAlert = null;
    },
    getAlertIcon(eventType) {
      switch (eventType) {
        case 'DUPLICATE_CARD_ID':
          return 'fa-id-card';
        case 'DUPLICATE_FINGERPRINT':
          return 'fa-fingerprint';
        case 'AUTHENTICATION_FAILURE':
          return 'fa-user-lock';
        case 'ABNORMAL_ACCESS':
          return 'fa-user-shield';
        default:
          return 'fa-exclamation-circle';
      }
    },
    getAlertTypeLabel(eventType) {
      switch (eventType) {
        case 'DUPLICATE_CARD_ID':
          return 'Carte en double';
        case 'DUPLICATE_FINGERPRINT':
          return 'Empreinte en double';
        case 'AUTHENTICATION_FAILURE':
          return 'Échec d\'authentification';
        case 'ABNORMAL_ACCESS':
          return 'Accès anormal';
        default:
          return eventType;
      }
    },
    getAlertTitle(alert) {
      switch (alert.eventType) {
        case 'DUPLICATE_CARD_ID':
          return `Carte en double détectée: ${alert.identifier}`;
        case 'DUPLICATE_FINGERPRINT':
          return `Empreinte en double détectée: ${alert.identifier}`;
        case 'AUTHENTICATION_FAILURE':
          return `Tentatives échouées d'authentification`;
        case 'ABNORMAL_ACCESS':
          return `Accès suspect détecté`;
        default:
          return `Alerte de sécurité: ${alert.eventType}`;
      }
    },
    getAlertDescription(alert) {
      switch (alert.eventType) {
        case 'DUPLICATE_CARD_ID':
          return `La carte ${alert.identifier} a été trouvée dans plusieurs tenants: ${alert.tenantsInvolved}`;
        case 'DUPLICATE_FINGERPRINT':
          return `L'empreinte digitale ${alert.identifier} a été trouvée dans plusieurs tenants: ${alert.tenantsInvolved}`;
        case 'AUTHENTICATION_FAILURE':
          return `Plusieurs tentatives échouées d'authentification depuis l'IP ${alert.ipAddress}`;
        case 'ABNORMAL_ACCESS':
          return `Accès détecté à des ressources inhabituelles depuis l'IP ${alert.ipAddress}`;
        default:
          return alert.additionalInfo || "Aucune description disponible";
      }
    },
    getStatusLabel(status) {
      switch (status) {
        case 'NEW':
          return 'Nouveau';
        case 'IN_PROGRESS':
          return 'En cours de traitement';
        case 'RESOLVED':
          return 'Résolu';
        default:
          return status;
      }
    },
    formatDate(dateString, detailed = false) {
      if (!dateString) return '';

      const date = new Date(dateString);

      if (detailed) {
        return new Intl.DateTimeFormat('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }).format(date);
      }

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 60) {
        return diffMins <= 1 ? 'À l\'instant' : `Il y a ${diffMins} minutes`;
      } else if (diffHours < 24) {
        return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
      } else if (diffDays < 7) {
        return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
      } else {
        return new Intl.DateTimeFormat('fr-FR', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);
      }
    },
    formatTime(date) {
      return new Intl.DateTimeFormat('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    }
  }
};
</script>

<style scoped>
.security-alerts-page {
  background-color: #f8f9fa;
  min-height: 100vh;
  padding: 20px;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #333;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  font-size: 28px;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-header h1 i {
  margin-right: 10px;
  color: #3498db;
}

.header-actions {
  display: flex;
  gap: 15px;
}

.btn-refresh {
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s;
}

.btn-refresh:hover {
  background-color: #2980b9;
}

.search-container {
  position: relative;
}

.search-container input {
  padding: 8px 12px 8px 35px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 250px;
  font-size: 14px;
}

.search-container i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.alert-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.stat-card {
  background-color: white;
  border-radius: 6px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.stat-card.has-alerts {
  border-left: 4px solid #e74c3c;
}

.stat-icon {
  background-color: #f5f5f5;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
}

.stat-icon i {
  font-size: 20px;
  color: #3498db;
}

.stat-card.has-alerts .stat-icon i {
  color: #e74c3c;
}

.stat-details h3 {
  margin: 0 0 5px 0;
  font-size: 24px;
  font-weight: 600;
}

.stat-details p {
  margin: 0;
  color: #7f8c8d;
  font-size: 14px;
}

.alerts-filter {
  background-color: white;
  border-radius: 6px;
  padding: 15px;
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-weight: 500;
  font-size: 14px;
  color: #555;
}

.filter-group select {
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.alerts-container {
  position: relative;
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.no-alerts {
  text-align: center;
  padding: 60px 0;
  color: #7f8c8d;
}

.no-alerts i {
  font-size: 48px;
  color: #2ecc71;
  margin-bottom: 15px;
}

.no-alerts h3 {
  font-size: 20px;
  margin: 0 0 10px 0;
}

.no-alerts p {
  margin: 0;
}

.alerts-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.alert-card {
  background-color: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-left: 4px solid #3498db;
  transition: transform 0.2s, box-shadow 0.2s;
}

.alert-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.alert-card.critical {
  border-left-color: #e74c3c;
}

.alert-card.warning {
  border-left-color: #f39c12;
}

.alert-card.resolved {
  border-left-color: #2ecc71;
  opacity: 0.7;
}

.alert-header {
  display: flex;
  align-items: center;
  background-color: #f8f9fa;
  padding: 10px 15px;
  border-bottom: 1px solid #eee;
}

.alert-icon {
  margin-right: 12px;
  font-size: 18px;
  color: #3498db;
}

.alert-card.critical .alert-icon {
  color: #e74c3c;
}

.alert-card.warning .alert-icon {
  color: #f39c12;
}

.alert-type {
  flex-grow: 1;
  font-weight: 500;
  font-size: 14px;
}

.alert-severity {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: lowercase;
  margin-right: 15px;
}

.alert-severity.critical {
  background-color: #ffebee;
  color: #e74c3c;
}

.alert-severity.warning {
  background-color: #fff3e0;
  color: #f39c12;
}

.alert-severity.info {
  background-color: #e3f2fd;
  color: #3498db;
}

.alert-actions {
  display: flex;
  gap: 8px;
}

.alert-actions button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: #7f8c8d;
  padding: 4px;
}

.alert-actions button:hover {
  color: #3498db;
}

.btn-details {
  color: #3498db;
}

.btn-resolve {
  color: #2ecc71;
}

.alert-content {
  padding: 15px;
}

.alert-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.alert-title {
  font-weight: 500;
  font-size: 16px;
}

.alert-date {
  color: #7f8c8d;
  font-size: 14px;
}

.alert-description {
  color: #555;
  font-size: 14px;
  margin-bottom: 15px;
  line-height: 1.5;
}

.alert-details {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
}

.detail-row {
  display: flex;
  margin-bottom: 8px;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.detail-label {
  flex: 0 0 150px;
  font-weight: 500;
  color: #666;
}

.detail-value {
  flex: 1;
}

/* Modal styles */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  width: 80%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #7f8c8d;
}

.close-btn:hover {
  color: #e74c3c;
}

.modal-body {
  padding: 20px;
}

.detail-section {
  margin-bottom: 25px;
}

.detail-section h3 {
  margin: 0 0 15px 0;
  font-size: 16px;
  color: #3498db;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}

.detail-item label {
  display: block;
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 5px;
}

.severity-badge, .status-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.severity-badge.critical {
  background-color: #ffebee;
  color: #e74c3c;
}

.severity-badge.warning {
  background-color: #fff3e0;
  color: #f39c12;
}

.status-badge.new {
  background-color: #e3f2fd;
  color: #3498db;
}

.status-badge.in_progress {
  background-color: #e8f5e9;
  color: #27ae60;
}

.status-badge.resolved {
  background-color: #f1f1f1;
  color: #7f8c8d;
}

.tenants-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tenant-item {
  background-color: #e3f2fd;
  color: #3498db;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 13px;
}

.additional-info {
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.5;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 25px;
}

.btn {
  padding: 10px 16px;
  border-radius: 4px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: background-color 0.2s;
}

.btn-resolve {
  background-color: #2ecc71;
  color: white;
}

.btn-resolve:hover {
  background-color: #27ae60;
}

.btn-investigate {
  background-color: #3498db;
  color: white;
}

.btn-investigate:hover {
  background-color: #2980b9;
}

.btn-report {
  background-color: #f8f9fa;
  color: #2c3e50;
  border: 1px solid #ddd;
}

.btn-report:hover {
  background-color: #f1f1f1;
}

/* Animations de transition */
.alert-list-enter-active, .alert-list-leave-active {
  transition: all 0.3s;
}

.alert-list-enter, .alert-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

/* Responsive design */
@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .search-container {
    width: 100%;
  }

  .search-container input {
    width: 100%;
  }

  .alerts-filter {
    flex-direction: column;
    gap: 15px;
  }

  .filter-group {
    width: 100%;
  }

  .filter-group select {
    flex: 1;
  }

  .alert-info {
    flex-direction: column;
    gap: 5px;
  }

  .action-buttons {
    flex-direction: column;
  }
}
</style>